/** `call` / `handoff` (design §8): another wizard, filled through ITS declared
    inputs (so the child works the same from its own menu entry), its outputs
    mapped back, and what happens for each way it can end. */

import ExprInput from './ExprInput'
import OpsEditor from './OpsEditor'
import { KeyValueEditor, ObjectPicker, StepPicker, TargetPicker } from './pickers'
import { useWizardDesign } from './context'
import type { WizardCallBlock, WizardCallOutcome, WizardReturnRoute } from './types'
import { OUTCOMES } from './types'
import {
  AddBtn, Badge, Check, Field, Grid2, RemoveBtn, SelectInput, Sub, TextInput, cardStyle, hintStyle,
} from './ui'

const OUTCOME_LABEL: Record<WizardCallOutcome, string> = {
  completed: 'Completed — the child saved',
  cancelled: 'Cancelled — the user backed out (or answered No)',
  pending_approval: 'Pending approval — the child’s save went to approval',
  pending_assignee: 'Pending assignee — a role completes it from the Work List',
  failed: 'Failed',
}

const CALL_TOKENS = [{ label: 'Call', tokens: ['{call.outcome}', '{call.outputs.txn_id}'] }]

function ReturnRouteEditor({ route, onChange, formNo }: {
  route: WizardReturnRoute
  onChange: (r: WizardReturnRoute | undefined) => void
  formNo?: string
}) {
  const patch = (p: Partial<WizardReturnRoute>) => onChange({ ...route, ...p })
  const where = route.goto ? 'goto' : 'resume'
  return (
    <div>
      <Grid2>
        <Field label="Then">
          <SelectInput value={where} onChange={(w) => (w === 'goto'
            ? onChange({ ...route, resume: undefined, goto: route.goto || '' })
            : onChange({ ...route, goto: undefined, resume: route.resume || 'same' }))}
            options={[{ value: 'resume', label: 'Resume' }, { value: 'goto', label: 'Go to a step' }]} />
        </Field>
        {where === 'resume' ? (
          <Field label="Resume at">
            <StepPicker value={route.resume || 'same'} onChange={(resume) => patch({ resume: resume || 'same' })}
              empty="" extra={[{ value: 'same', label: 'the calling step (same)' }, { value: 'next', label: 'the step after it (next)' }]} />
          </Field>
        ) : (
          <Field label="Go to"><StepPicker value={route.goto} onChange={(goto) => patch({ goto })} /></Field>
        )}
      </Grid2>
      <Check checked={route.suspend} onChange={(suspend) => patch({ suspend: suspend || undefined })}
        label="Suspend the run until it is decided (needs suspend: server)" />
      <Field label="Message">
        <ExprInput mode="template" value={route.message} onChange={(message) => patch({ message: message || undefined })}
          formNo={formNo} extraTokens={CALL_TOKENS} />
      </Field>
      <Field label="Refresh before resuming (item change / fetch / recalc / lookup)">
        <OpsEditor ops={route.refresh} onChange={(refresh) => patch({ refresh: refresh.length ? refresh : undefined })}
          formNo={formNo} extraTokens={CALL_TOKENS} emptyText="Nothing re-read." />
      </Field>
    </div>
  )
}

export function CallBlockEditor({ block, onChange, formNo, handoff = false }: {
  block: WizardCallBlock & { require_saved?: boolean }
  onChange: (b: WizardCallBlock & { require_saved?: boolean }) => void
  formNo?: string
  handoff?: boolean
}) {
  const ctx = useWizardDesign()
  const patch = (p: Partial<WizardCallBlock & { require_saved?: boolean }>) => onChange({ ...block, ...p })
  const child = ctx.catalog?.wizards.find((w) => w.obj_name === block.wizard) || null
  const inputs = child?.inputs || []
  const required = inputs.filter((i) => i.required).map((i) => i.name)
  const lockable = inputs.filter((i) => i.lockable).map((i) => i.name)
  const runAs = block.run_as && typeof block.run_as === 'object' ? block.run_as.role : ''
  const self = block.wizard && block.wizard === ctx.model.obj_name
  return (
    <div>
      <Field label="Wizard">
        <ObjectPicker objType="U" value={block.wizard} onChange={(wizard) => patch({ wizard })} />
        {self && <div style={{ ...hintStyle, color: 'var(--color-error, #d33)' }}>A wizard cannot call itself.</div>}
        {child && (
          <div style={hintStyle}>
            {child.title || child.obj_name} · {child.mode || 'add'} on {child.base_object || '?'}
            {' · '}
            <a href={`/designers/wizard/${encodeURIComponent(ctx.project)}/${encodeURIComponent(child.obj_name)}`}
              target="_blank" rel="noopener noreferrer">open its designer</a>
          </div>
        )}
      </Field>
      {!handoff && (
        <Field label="Ask first (Yes runs it, No routes as cancelled)">
          <ExprInput mode="template" value={block.ask} onChange={(ask) => patch({ ask: ask || undefined })} formNo={formNo}
            placeholder="Record a stock adjustment?" />
        </Field>
      )}
      <Field label="Inputs handed over (the child's input → template)">
        <KeyValueEditor value={block.with} onChange={(w) => patch({ with: w })} formNo={formNo}
          keyOptions={inputs.map((i) => ({
            value: i.name,
            label: `${i.label ? `${i.name} — ${i.label}` : i.name}${i.required ? ' *' : ''}`,
          }))}
          requiredKeys={required} keyPlaceholder="input" />
        {!child && block.wizard && <div style={hintStyle}>That wizard is not in the catalog — its inputs cannot be listed.</div>}
      </Field>
      {handoff ? (
        <Check checked={block.require_saved !== false} onChange={(v) => patch({ require_saved: v })}
          label="Refuse to hand off with unsaved changes" />
      ) : (
        <>
          {lockable.length > 0 && (
            <Field label="Lock these inputs (shown read-only in the child)">
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {lockable.map((n) => {
                  const on = (block.lock_inputs || []).includes(n)
                  return (
                    <label key={n} style={{ fontSize: 13.5 }}>
                      <input type="checkbox" checked={on} onChange={() => patch({
                        lock_inputs: on ? (block.lock_inputs || []).filter((x) => x !== n) : [...(block.lock_inputs || []), n],
                      })} /> {n}
                    </label>
                  )
                })}
              </div>
            </Field>
          )}
          <Grid2>
            <Field label="Run as">
              <SelectInput value={runAs ? 'role' : 'self'}
                onChange={(v) => patch({ run_as: v === 'role' ? { role: runAs || '' } : 'self' })}
                options={[{ value: 'self', label: 'This user' }, { value: 'role', label: 'A role (Work List task)' }]} />
            </Field>
            {runAs !== '' || (block.run_as && typeof block.run_as === 'object') ? (
              <Field label="Role">
                <TextInput value={runAs} onChange={(role) => patch({ run_as: { role } })} placeholder="inventory-supervisor" />
              </Field>
            ) : null}
          </Grid2>
          <Field label="Results brought back (target here → the child's output)">
            <KeyValueEditor value={block.returns} onChange={(r) => patch({ returns: r as Record<string, string> })}
              valueMode="text" valuePlaceholder="output"
              keyRender={(k, setK) => <TargetPicker value={k} onChange={setK} formNo={formNo} />} />
            {child && child.outputs.length > 0 && (
              <div style={hintStyle}>The child's outputs: {child.outputs.join(', ')}</div>
            )}
          </Field>
          <Sub title="When it returns">
            {OUTCOMES.map((o) => {
              const route = block.on_return?.[o]
              return (
                <div key={o} style={{ ...cardStyle, padding: 8, marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <span style={{ fontSize: 13.5 }}>
                      {OUTCOME_LABEL[o]}{' '}
                      {!route && o !== 'pending_assignee' && <Badge tone="warn">no route</Badge>}
                    </span>
                    {!ctx.readOnly && (route
                      ? <RemoveBtn onClick={() => {
                        const next = { ...(block.on_return || {}) }
                        delete next[o]
                        patch({ on_return: next })
                      }} />
                      : <AddBtn onClick={() => patch({ on_return: { ...(block.on_return || {}), [o]: { resume: 'same' } } })}>Route</AddBtn>)}
                  </div>
                  {route && (
                    <div style={{ marginTop: 6 }}>
                      <ReturnRouteEditor route={route} formNo={formNo}
                        onChange={(r) => patch({ on_return: { ...(block.on_return || {}), [o]: r } })} />
                    </div>
                  )}
                </div>
              )
            })}
          </Sub>
        </>
      )}
    </div>
  )
}
